// src/components/customer/CustomerOrders.js
import { useState } from 'react';
import { Package, Search, ChevronDown, ChevronUp, Truck, Clock, CheckCircle, XCircle, FileText } from 'lucide-react';

const STATUS_CONFIG = {
  'New': { color: 'bg-blue-100 text-blue-800', icon: Clock },
  'Processing': { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  'Filled': { color: 'bg-purple-100 text-purple-800', icon: Package },
  'Shipped': { color: 'bg-indigo-100 text-indigo-800', icon: Truck },
  'Delivered': { color: 'bg-green-100 text-green-800', icon: CheckCircle },
  'Cancelled': { color: 'bg-red-100 text-red-800', icon: XCircle }
};

export function CustomerOrders({ orders, loading }) {
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-gray-300 border-t-black rounded-full mx-auto"></div>
        <p className="text-gray-500 mt-4">Loading orders...</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Order History</h2>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by order ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="all">All Statuses</option>
            {Object.keys(STATUS_CONFIG).map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Orders List */}
      {filteredOrders.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Package size={48} className="text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600">No orders found</h3>
          <p className="text-gray-500">
            {searchTerm || statusFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'Your orders will appear here'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map(order => {
            const isExpanded = expandedOrder === order.id;
            const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG['New'];
            const StatusIcon = statusConfig.icon;
            const orderDate = order.createdAt?.toDate?.() || new Date(order.createdAt);

            return (
              <div key={order.id} className="bg-white rounded-lg shadow overflow-hidden">
                {/* Order Header */}
                <button
                  onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4 text-left">
                    <div>
                      <p className="font-semibold">Order #{order.id.slice(-8).toUpperCase()}</p>
                      <p className="text-sm text-gray-500">{orderDate.toLocaleDateString()}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 ${statusConfig.color}`}>
                      <StatusIcon size={14} />
                      {order.status}
                    </span>
                    <span className="font-bold">${(order.total || order.subtotal)?.toFixed(2)}</span>
                    {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </div>
                </button>

                {/* Order Details */}
                {isExpanded && (
                  <div className="border-t p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Items */}
                      <div>
                        <h4 className="font-semibold mb-3">Items</h4>
                        <div className="space-y-2">
                          {order.items?.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span>{item.name} × {item.quantity}</span>
                              <span>${(item.wholesalePrice * item.quantity).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>

                        <div className="border-t mt-3 pt-3 space-y-1">
                          <div className="flex justify-between text-sm">
                            <span>Subtotal</span>
                            <span>${order.subtotal?.toFixed(2)}</span>
                          </div>
                          {order.ccFee > 0 && (
                            <div className="flex justify-between text-sm text-gray-500">
                              <span>CC Fee</span>
                              <span>${order.ccFee?.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between font-bold">
                            <span>Total</span>
                            <span>${(order.total || order.subtotal)?.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Shipping */}
                      <div>
                        <h4 className="font-semibold mb-3">Shipping</h4>
                        {order.shippingLocation ? (
                          <div className="text-sm text-gray-600">
                            <p className="font-medium">{order.shippingLocation.name}</p>
                            <p>{order.shippingLocation.street}</p>
                            <p>{order.shippingLocation.city}, {order.shippingLocation.state} {order.shippingLocation.zip}</p>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No shipping info</p>
                        )}

                        {order.trackingNumber && (
                          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                            <p className="text-sm font-medium text-blue-800">Tracking Number</p>
                            <p className="text-sm text-blue-600">{order.trackingNumber}</p>
                          </div>
                        )}
                      </div>

                      {/* Payment */}
                      <div>
                        <h4 className="font-semibold mb-3">Payment</h4>
                        <p className="text-sm">
                          <span className="text-gray-500">Method: </span>
                          {order.paymentMethod === 'customerCC' && 'Credit Card'}
                          {order.paymentMethod === 'customerInvoice' && 'Invoice'}
                          {order.paymentMethod === 'repCC' && 'Rep Credit Card'}
                        </p>
                        <p className="text-sm">
                          <span className="text-gray-500">Status: </span>
                          <span className={`${
                            order.paymentStatus === 'succeeded' || order.paymentStatus === 'paid'
                              ? 'text-green-600 font-medium'
                              : 'text-yellow-600'
                          }`}>
                            {order.paymentStatus === 'succeeded' || order.paymentStatus === 'paid'
                              ? 'Paid'
                              : order.paymentStatus === 'pending_invoice'
                              ? 'Invoice Pending'
                              : 'Pending'}
                          </span>
                        </p>

                        {order.notes && (
                          <div className="mt-4">
                            <p className="text-sm font-medium">Notes</p>
                            <p className="text-sm text-gray-600">{order.notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
